import type { ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';
import { Badge } from '../ui/badge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import { cn } from '../../lib/utils';

/**
 * The width-affecting badge classes every association chip shares. Exported
 * so the strip that hosts the chips (`AgentAssociationChips` in web) can
 * measure a text-free lookalike without the rendered widths drifting. Chips
 * built on {@link AssociationChip} get these automatically; a plugin chip
 * that draws its own badge should reuse them for the same reason.
 */
export const ASSOCIATION_CHIP_BADGE_CLASSNAME =
  'cursor-pointer gap-0.5 border-border bg-muted/50 px-1.5 py-0 text-[10px] font-medium text-muted-foreground hover:bg-muted';

export interface AssociationChipProps {
  /** Link target; renders a non-navigating chip when null. */
  href: string | null;
  /** Compact badge text (the item's key: `ENG-123`, `#42`, ...). */
  label: string;
  ariaLabel: string;
  /** Small icon inside the badge. */
  icon: ReactNode;
  /** Popover heading (defaults to the badge label). */
  hoverTitle?: string;
  /** Popover second line — typically the item's title. */
  hoverSubtitle?: string | null;
  /** Popover detail line — the agent's relationship to the item. */
  hoverDetail?: string | null;
  /** Label of the popover's external link (e.g. `View in Linear`). */
  linkLabel?: string;
  /** Badge class overrides layered on {@link ASSOCIATION_CHIP_BADGE_CLASSNAME}. */
  badgeClassName?: string;
  /**
   * Collapse the chip to its icon and border, dropping the visible label, when
   * the host strip runs out of room. The label still rides in `ariaLabel` and
   * the hover popover, so nothing is lost; the chip just stops competing with
   * the row title for horizontal space.
   */
  iconOnly?: boolean;
  'data-testid'?: string;
}

/**
 * Shared shell for the compact chips that show an agent's linked work items.
 * Owns the badge + hover-popover chrome so the core chip and every
 * plugin-registered chip render consistently; callers supply the kind's
 * icon, label, and link. Kind-specific details (title, url) come from the
 * association's `data` payload, which the same provider wrote at record
 * time.
 */
export function AssociationChip({
  href,
  label,
  ariaLabel,
  icon,
  hoverTitle,
  hoverSubtitle,
  hoverDetail,
  linkLabel,
  badgeClassName,
  iconOnly,
  'data-testid': dataTestId,
}: AssociationChipProps) {
  const badge = (
    <Badge variant="outline" className={cn(ASSOCIATION_CHIP_BADGE_CLASSNAME, badgeClassName)}>
      {icon}
      {iconOnly ? null : <span>{label}</span>}
    </Badge>
  );

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={ariaLabel}
            data-testid={dataTestId ?? 'association-chip'}
            className="inline-flex items-center rounded-md align-middle transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {badge}
          </a>
        ) : (
          <span
            aria-label={ariaLabel}
            data-testid={dataTestId ?? 'association-chip'}
            className="inline-flex items-center rounded-md align-middle"
          >
            {badge}
          </span>
        )}
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-64" data-testid="association-chip-popover">
        <div className="flex items-start gap-2">
          <span aria-hidden="true" className="mt-0.5 shrink-0 [&>svg]:h-4 [&>svg]:w-4">
            {icon}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{hoverTitle ?? label}</p>
            {hoverSubtitle ? (
              <p className="truncate text-xs text-muted-foreground">{hoverSubtitle}</p>
            ) : null}
            {hoverDetail ? (
              <p className="text-xs text-muted-foreground" data-testid="association-chip-detail">
                {hoverDetail}
              </p>
            ) : null}
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={linkLabel ?? 'Open link'}
                className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline"
              >
                {linkLabel ?? 'Open link'}
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            ) : null}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
